import { HTMLAttributes } from 'react'

import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import HeroParallax from '@/components/base/hero-parallax'
import Media from '@/components/base/media'
import ScrollTo from '@/components/base/scroll-to'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'
import { useLoaderStore } from '@/store/loader'

import { useHeroMotion } from './motion/hero'
import { cn } from '@/lib/utils'
import { WPMedia } from '@/types'

export type HeroProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    label: string
    pretitle: string
    title: string
    media: WPMedia
  }>

const Hero = ({ label, pretitle, title, media, ...props }: HeroProps) => {
  const { motionRef } = useHeroMotion()
  const setLoaderState = useLoaderStore((state) => state.setState)

  const handleLoad = () => {
    setLoaderState({ pageLoaded: true, loaded: true })
  }

  return (
    <Section ref={motionRef} {...props}>
      <Grid className="h-[112vh]">
        {media ? (
          <HeroParallax
            className={cn(
              'relative col-[1/-1] row-[1/-1] overflow-hidden',
              'after:absolute after:left-0 after:top-0 after:size-full after:bg-black/15'
            )}
          >
            <span className="flex size-full">
              <Media
                onLoad={handleLoad}
                className="object-cover object-bottom"
                lazy={false}
                {...media}
                data-motion-media
              />
            </span>
          </HeroParallax>
        ) : null}

        <Container className="h-svh-vh relative col-[1/-1] row-[1/-1] flex items-center justify-center">
          <Grid>
            <div
              className={cn(
                'col-span-2 md:col-span-5 lg:col-span-8 xl:col-span-6',
                'grid gap-y-[1rem]'
              )}
            >
              <div className="grid gap-y-[3rem]">
                {label ? (
                  <Typography
                    variant="upper-small"
                    paragraph
                    className="font-bold uppercase text-white"
                    data-motion-label
                  >
                    {label}
                  </Typography>
                ) : null}
                {pretitle ? (
                  <Typography
                    variant="main-title-default"
                    paragraph
                    className="text-white"
                    data-motion-pretitle
                  >
                    {pretitle}
                  </Typography>
                ) : null}
              </div>

              {title ? (
                <Typography
                  variant="hero-tb-customized"
                  component="h1"
                  className={cn('capitalize text-white', 'font-light [&_strong]:font-medium')}
                  data-motion-title
                >
                  {title}
                </Typography>
              ) : null}
            </div>
          </Grid>

          <ScrollTo
            className="absolute bottom-[5rem] left-1/2 -translate-x-1/2 md:bottom-[6rem]"
            data-motion-scroll
          />
        </Container>
      </Grid>
    </Section>
  )
}

export default Hero
